import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import { Card, IconButton, Stack, styled, Typography } from '@mui/material';
import {
  FMoneyInput,
  FTextInput,
  Iconify,
  LoadingPromiseButtonAdvanced,
  Modal,
  ModalInner,
  useErrorSnackbar,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import type { FormikHelpers } from 'formik';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import type {
  CreateLineItemConfigurationDto,
  TaxCodeResultDto,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import {
  useCreateLineItemConfigurationPhoenix,
  useGetTaxCodesByCountryPhoenix,
  useServiceProvider,
} from '@pflegenavi/service-provider-frontend/api';
import { useGetCountryOfServiceProvider } from '../../hooks/useGetCountryOfServiceProvider';
import { sanitizeString } from '@pflegenavi/shared/utils';
import { useGetGlobalServiceProviderId } from '../../hooks/useGetGlobalServiceProviderId';
import {
  MAX_SERVICE_PROVIDER_STRIPE_PAYMENT_CENT_AMOUNT,
  MAX_TRANSACTION_AMOUNT_IN_CENTS,
} from '@pflegenavi/shared/constants';
import { PaymentProcessor } from '@pflegenavi/shared/api';
import { FServiceTaxCodePicker } from './FServiceTaxCodePicker';

export interface AddNewServiceModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const AddNewServiceModal: FC<AddNewServiceModalProps> = ({
  open,
  setOpen,
}) => {
  const handleClose = (_: any, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason !== 'backdropClick') {
      setOpen(false);
    }
  };

  const { globalServiceProviderId } = useGetGlobalServiceProviderId();

  const countryId = useGetCountryOfServiceProvider();

  const { data: taxCodes } = useGetTaxCodesByCountryPhoenix(countryId);
  const { mutateAsync: addNewService } = useCreateLineItemConfigurationPhoenix(
    globalServiceProviderId
  );

  const handleAddNewService = useCallback(
    async (data: CreateLineItemConfigurationDto) => {
      await addNewService(data);
    },
    [addNewService]
  );

  return (
    <Modal open={open} handleClose={handleClose}>
      <ModalInner>
        <AddNewServiceForm
          setOpen={setOpen}
          handleAddNewService={handleAddNewService}
          taxCodes={taxCodes ?? []}
        />
      </ModalInner>
    </Modal>
  );
};

export interface IAddNewServiceModalValues {
  serviceName: string | undefined;
  amount: number | undefined;
  taxCodes: string[] | undefined;
}

const myInitialValues: IAddNewServiceModalValues = {
  serviceName: undefined,
  amount: undefined,
  taxCodes: undefined,
};

export interface AddNewServiceFormProps {
  setOpen: (open: boolean) => void;
  handleAddNewService: (data: CreateLineItemConfigurationDto) => Promise<void>;
  taxCodes: TaxCodeResultDto[];
}

export const AddNewServiceForm: FC<AddNewServiceFormProps> = ({
  setOpen,
  handleAddNewService,
  taxCodes,
}) => {
  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();

  const { globalServiceProviderId } = useGetGlobalServiceProviderId();
  const { data: serviceProvider } = useServiceProvider({
    globalServiceProviderId,
  });

  const closeModal = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleCreateServiceProvider = useCallback(
    async (
      values: IAddNewServiceModalValues,
      formikHelpers: FormikHelpers<IAddNewServiceModalValues>
    ) => {
      const { serviceName, taxCodes, amount } = values;

      if (!serviceName || !amount) {
        throw new Error(
          t(
            'service-provider-page.services.add-modal.form.errors.fill-all-fields'
          )
        );
      }
      try {
        formikHelpers.setSubmitting(true);
        await handleAddNewService({
          name: sanitizeString(serviceName),
          amount: amount,
          taxCodes: taxCodes,
        });
        enqueueSnackbar(
          t('service-provider-page.services.add-modal.success-adding'),
          {
            variant: 'success',
          }
        );
        closeModal();
      } catch (error) {
        enqueueErrorSnackbar(
          error,
          t('service-provider-page.services.add-modal.error-adding')
        );
      } finally {
        formikHelpers.setSubmitting(false);
      }
    },
    [t, handleAddNewService, enqueueSnackbar, closeModal, enqueueErrorSnackbar]
  );

  const validationSchema = useMemo(() => {
    return Yup.object({
      serviceName: Yup.string()
        .required(
          t(
            'service-provider-page.services.add-modal.form.required.service-name'
          )
        )
        .min(
          1,
          t('service-provider-page.services.add-modal.form.valid.service-name')
        )
        .max(255, t('field.max-length', { length: 255 }))
        .test(
          'no-empty',
          t(
            'service-provider-page.services.add-modal.form.required.service-name'
          ),
          (value) => {
            if (value) {
              return value.trim() !== ''; // Returns false if the value is empty or contains only spaces.
            }
            return true;
          }
        ),
      amount: Yup.number()
        .required(
          t('service-provider-page.services.add-modal.form.required.amount')
        )
        .min(
          1,
          t('service-provider-page.services.add-modal.form.min-amount.amount')
        ),
    });
  }, [t]);

  const formik = useFormik({
    initialValues: {
      ...myInitialValues,
      taxCodes: serviceProvider?.defaultTaxCodeId
        ? [serviceProvider.defaultTaxCodeId]
        : undefined,
    },
    enableReinitialize: true,
    onSubmit: handleCreateServiceProvider,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
  });

  return (
    <FormCard>
      <Stack gap={3}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6" fontWeight={700}>
            {t('service-provider-page.services.add-modal.title')}
          </Typography>
          <IconButton
            onClick={closeModal}
            sx={{
              padding: '16px',
              marginRight: '-8px',
            }}
          >
            <Iconify
              icon={'eva:close-fill'}
              sx={{
                position: 'absolute',
              }}
              width={20}
            />
          </IconButton>
        </Stack>
        <Stack gap={2}>
          <FTextInput
            id="serviceName"
            name="serviceName"
            label={t('service-provider-page.services.add-modal.service-name')}
            formik={formik}
          />
          <FMoneyInput
            name="amount"
            formik={formik}
            label={t('service-provider-page.services.add-modal.amount')}
            placeholder={t('service-provider-page.services.add-modal.amount')}
            maxValue={
              serviceProvider?.paymentProcessor === PaymentProcessor.Mangopay
                ? MAX_TRANSACTION_AMOUNT_IN_CENTS
                : MAX_SERVICE_PROVIDER_STRIPE_PAYMENT_CENT_AMOUNT
            }
          />
          <FServiceTaxCodePicker
            id="taxRate"
            name="taxCodes"
            formik={formik}
            taxCodes={taxCodes}
            label={t('service-provider-page.services.add-modal.tax-rate')}
          />
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <LoadingPromiseButtonAdvanced
            variant="contained"
            disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
            onClick={formik.submitForm}
          >
            {t('service-provider-page.services.add-modal.add-button')}
          </LoadingPromiseButtonAdvanced>
        </Stack>
      </Stack>
    </FormCard>
  );
};

const FormCard = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    p: 3,
    minWidth: 600,
    width: 700,
    maxWidth: '100%',
  })
);
